// Model procházení výprodeje (saleBrowse.js) — čisté funkce bez DOM, aby šly
// testovat v Node (tools/unit/sale-browse.test.mjs).
//
// Stav výřezu = `{cat, sub, disc, pmin, pmax, brands, last, clearance, order}`.
// Žije v adrese stránky (jména parametrů a hodnoty řazení jsou text jazyka
// shopu, `locale/<jazyk>/saleBrowse.js`), do API jde jako `GET /browse`
// (search/api/routes-browse.mjs). Fasety API jsou disjunktivní: počty skupiny
// se počítají bez jejího vlastního filtru, takže dlaždice kořenů drží počty
// i po výběru kategorie.

import L from '../locale/cs/saleBrowse.js';

/** Řazení v pořadí záložek; první je výchozí (v adrese se nepíše). */
export const ORDERS = ['bestseller', 'discount', 'price_asc', 'price_desc'];
/** Pásma slevy ve filtru (API je počítá z fasety `discount_pct`). */
export const DISCOUNT_BANDS = [20, 30, 40];
/** Dávka karet (plátno: „Načítání po 24"). */
export const PAGE_SIZE = 24;
/** Kolik značek ukáže filtr před „Zobrazit více" (jako nativní sekce, SEC_LIMIT). */
export const BRAND_LIMIT = 5;

const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,79}$/;

export function emptyState() {
  return { cat: null, sub: null, disc: null, pmin: null, pmax: null, brands: [], last: false, clearance: false, order: ORDERS[0] };
}

function slug(value) {
  const v = String(value || '').toLowerCase();
  return SLUG_RE.test(v) ? v : null;
}

function price(value) {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** `/zahrada/` → `zahrada` (slug kategorie = cesta bez lomítek). */
export function slugOf(path) {
  return String(path || '').replace(/^\/+|\/+$/g, '');
}

/**
 * Stav z query stringu stránky. Neznámé a nevalidní hodnoty se zahodí (adresu
 * může kdokoli upravit ručně), cizí parametry se ignorují.
 *
 * @param {string} search `location.search`
 * @param {{params?: object, orderSlugs?: object}} [t] texty jazyka (výchozí CZ / bundle)
 */
export function parseState(search, t) {
  const P = (t && t.params) || L.params;
  const O = (t && t.orderSlugs) || L.orderSlugs;
  const q = new URLSearchParams(search || '');
  const s = emptyState();
  s.cat = slug(q.get(P.cat));
  s.sub = s.cat ? slug(q.get(P.sub)) : null;
  const disc = parseInt(q.get(P.disc), 10);
  s.disc = DISCOUNT_BANDS.indexOf(disc) >= 0 ? disc : null;
  const range = String(q.get(P.price) || '').split('-');
  if (range.length === 2) {
    s.pmin = price(range[0]);
    s.pmax = price(range[1]);
    if (s.pmin !== null && s.pmax !== null && s.pmin > s.pmax) {
      const swap = s.pmin;
      s.pmin = s.pmax;
      s.pmax = swap;
    }
  }
  const brands = String(q.get(P.brand) || '')
    .split(',')
    .map((b) => b.trim())
    .filter(Boolean);
  s.brands = brands.filter((b, i) => brands.indexOf(b) === i);
  s.last = q.get(P.last) === '1';
  s.clearance = q.get(P.clearance) === '1';
  const order = Object.keys(O).find((k) => O[k] === q.get(P.order));
  s.order = order && ORDERS.indexOf(order) >= 0 ? order : ORDERS[0];
  return s;
}

/**
 * Query string se stavem: naše parametry se přepíšou (výchozí hodnoty se
 * vynechají), cizí (UTM, gclid) zůstávají. Vrací `''` nebo `?…`.
 */
export function writeQuery(search, state, t) {
  const P = (t && t.params) || L.params;
  const O = (t && t.orderSlugs) || L.orderSlugs;
  const q = new URLSearchParams(search || '');
  Object.keys(P).forEach((k) => q.delete(P[k]));
  const s = state || emptyState();
  if (s.cat) q.set(P.cat, s.cat);
  if (s.cat && s.sub) q.set(P.sub, s.sub);
  if (s.clearance) q.set(P.clearance, '1');
  if (s.disc) q.set(P.disc, String(s.disc));
  if (s.pmin !== null || s.pmax !== null) q.set(P.price, (s.pmin === null ? '' : s.pmin) + '-' + (s.pmax === null ? '' : s.pmax));
  if (s.brands.length) q.set(P.brand, s.brands.join(','));
  if (s.last) q.set(P.last, '1');
  if (s.order && s.order !== ORDERS[0]) q.set(P.order, O[s.order]);
  const out = q.toString();
  return out ? '?' + out : '';
}

/** Parametry `GET /browse` (kontrakt search/api/routes-browse.mjs). */
export function apiParams(state, offset, limit) {
  const s = state || emptyState();
  return {
    scope: 'sale',
    cat: s.cat || '',
    sub: s.cat && s.sub ? s.sub : '',
    brand: s.brands.join(','),
    disc: s.disc || '',
    pmin: s.pmin === null ? '' : s.pmin,
    pmax: s.pmax === null ? '' : s.pmax,
    last: s.last ? '1' : '',
    clearance: s.clearance ? '1' : '',
    order: s.order || ORDERS[0],
    offset: offset || 0,
    limit: limit || PAGE_SIZE,
  };
}

/** Je odpověď API použitelná? Jinak zůstává nativní výpis (fallback). */
export function validResponse(data) {
  return (
    !!data &&
    typeof data.total === 'number' &&
    Array.isArray(data.products) &&
    !!data.facets &&
    Array.isArray(data.facets.roots)
  );
}

/**
 * Dlaždice kořenů v pořadí menu (`catalog.roots` manifestu), kořeny mimo
 * manifest za nimi podle API. API vrací jen kořeny s aspoň 5 produkty ve
 * výprodeji; dlaždice s nulou po filtru zůstává (neskáčou), jen je neaktivní.
 *
 * @param {Array<{slug: string, path: string, name: string, count: number}>} roots
 * @param {Object<string, string>} manifestRoots `{garden: '/zahrada/', …}`
 * @param {object} state
 */
export function tilesModel(roots, manifestRoots, state) {
  const keys = Object.keys(manifestRoots || {});
  const keyOf = (path) => keys.find((k) => manifestRoots[k] === path) || null;
  const rank = (path) => {
    const i = keys.findIndex((k) => manifestRoots[k] === path);
    return i < 0 ? keys.length : i;
  };
  return (roots || [])
    .map((r, i) => ({ r, i }))
    .sort((a, b) => rank(a.r.path) - rank(b.r.path) || a.i - b.i)
    .map(({ r }) => ({
      slug: r.slug || slugOf(r.path),
      path: r.path,
      key: keyOf(r.path),
      name: r.name,
      count: Number(r.count) || 0,
      active: !!state && state.cat === (r.slug || slugOf(r.path)),
    }));
}

/** Čipy podkategorií vybraného kořene (pořadí API = počet sestupně). */
export function chipsModel(subs, state) {
  return (subs || []).map((s) => ({
    slug: s.slug || slugOf(s.path),
    path: s.path,
    name: s.name,
    count: Number(s.count) || 0,
    active: !!state && state.sub === (s.slug || slugOf(s.path)),
  }));
}

/** Text čipu „Vše v Zahradě" pro kořen (`key` = klíč `catalog.roots`). */
export function allInLabel(key, t) {
  const T = t || L;
  return (key && T.allIn[key]) || T.allFallback;
}

/**
 * Filtry panelu jako čipy (lišta na mobilu, řádek čipů ve filtru): sleva,
 * cena, značky, poslední kusy. Kategorie a výprodej skladu jsou dlaždice,
 * ne filtr panelu. `fmt` = formátování částky (money.js).
 *
 * @returns {Array<{key: string, value?: string, label: string}>}
 */
export function panelFilters(state, fmt, t) {
  const T = t || L;
  const s = state || emptyState();
  const out = [];
  if (s.disc) out.push({ key: 'disc', label: T.discFrom(s.disc) });
  if (s.pmin !== null || s.pmax !== null) {
    const from = s.pmin === null ? '' : fmt(s.pmin);
    const to = s.pmax === null ? '' : fmt(s.pmax);
    out.push({ key: 'price', label: T.priceRange(from, to).trim() });
  }
  s.brands.forEach((b) => out.push({ key: 'brand', value: b, label: b }));
  if (s.last) out.push({ key: 'last', label: T.lastPieces });
  return out;
}

/** Stav bez jednoho filtru panelu (odebrání čipu). */
export function withoutFilter(state, key, value) {
  const s = Object.assign({}, state, { brands: state.brands.slice() });
  if (key === 'disc') s.disc = null;
  else if (key === 'price') {
    s.pmin = null;
    s.pmax = null;
  } else if (key === 'brand') s.brands = s.brands.filter((b) => b !== value);
  else if (key === 'last') s.last = false;
  return s;
}

/** Stav bez filtrů panelu; kategorie, výprodej skladu a řazení zůstávají. */
export function clearPanel(state) {
  return Object.assign({}, state, { disc: null, pmin: null, pmax: null, brands: [], last: false });
}

/** Má výběr něco nad celým výprodejem (prázdný stav nabízí „Zrušit filtry")? */
export function hasSelection(state) {
  const s = state || emptyState();
  return !!(s.cat || s.clearance || panelFilters(s, String).length);
}

/** Stav bez všeho, co zužuje výpis (řazení zůstává). */
export function clearAll(state) {
  return Object.assign(clearPanel(state), { cat: null, sub: null, clearance: false });
}

/**
 * Pásma slevy pro radia: `[{min, count, checked}]` z fasety API. Pásmo bez
 * produktu se nekreslí, vybrané pásmo zůstává vždy (jinak by šlo zrušit jen čipem).
 */
export function discountRows(facet, state) {
  const byMin = {};
  (facet || []).forEach((f) => {
    byMin[f.min] = Number(f.count) || 0;
  });
  return DISCOUNT_BANDS.map((min) => ({ min, count: byMin[min] || 0, checked: !!state && state.disc === min })).filter(
    (r) => r.count > 0 || r.checked,
  );
}

/**
 * Značky pro checkboxy: fasety API (počet sestupně) + vybrané značky, které
 * ve fasetě nejsou (počet 0), aby šly odškrtnout.
 */
export function brandRows(facet, state) {
  const rows = (facet || []).map((f) => ({ name: f.name, count: Number(f.count) || 0 }));
  const have = rows.map((r) => r.name);
  ((state && state.brands) || []).forEach((b) => {
    if (have.indexOf(b) < 0) rows.push({ name: b, count: 0 });
  });
  return rows.map((r) => Object.assign(r, { checked: !!state && state.brands.indexOf(r.name) >= 0 }));
}

/**
 * Rozsah cenového posuvníku: hranice z fasety (bez cenového filtru, takže se
 * nezužuje po každém posunu), úchyty na zvolené hodnoty oříznuté do hranic.
 * `null` = cena se nekreslí (nic k filtrování).
 */
export function priceRange(facet, state) {
  if (!facet || !Number.isFinite(facet.min) || !Number.isFinite(facet.max) || facet.max <= facet.min) return null;
  const clamp = (v) => Math.min(facet.max, Math.max(facet.min, v));
  return {
    min: facet.min,
    max: facet.max,
    from: state && state.pmin !== null ? clamp(state.pmin) : facet.min,
    to: state && state.pmax !== null ? clamp(state.pmax) : facet.max,
  };
}

/**
 * Hodnoty posuvníku → stav: úchyt na hranici = filtr na té straně neplatí
 * (celý rozsah = žádný cenový filtr).
 */
export function withPrice(state, range, from, to) {
  const lo = Math.min(from, to);
  const hi = Math.max(from, to);
  return Object.assign({}, state, {
    pmin: lo <= range.min ? null : Math.round(lo),
    pmax: hi >= range.max ? null : Math.round(hi),
  });
}

/**
 * Štítky karet z indexu jako na nativní kartě: index posílá `flags` jako holé
 * `akce` / `sleva` (card.mjs z `badges`), nativní karta má vlaječku s třídou
 * `flag-action` a příznak výprodeje skladu. productCard.js je pak zúží na jeden
 * akční štítek (výprodej před akcí, po dobu kampaně název deštníku) a slevu
 * kreslí z přeškrtnuté ceny jako „−N %", takže `sleva` odpadá.
 *
 * @param {object[]} cards karty `/browse` (`clearance: true` = výprodej skladu)
 * @param {string|null} clearanceClass třída příznaku výprodeje (`catalog.clearanceFlag`)
 */
export function presentCards(cards, clearanceClass, t) {
  const T = t || L;
  const clearanceType = clearanceClass ? String(clearanceClass).replace(/^flag-/, '') : null;
  return (cards || []).map(function (c) {
    const raw = Array.isArray(c.flags) ? c.flags : [];
    const flags = [];
    if (c.clearance === true && clearanceType) flags.push({ type: clearanceType, title: T.clearanceTitle });
    if (raw.indexOf('akce') >= 0) flags.push({ type: 'action', title: T.flagAction });
    raw.forEach(function (f) {
      if (f !== 'akce' && f !== 'sleva') flags.push(f);
    });
    return Object.assign({}, c, { flags: flags });
  });
}

/** Text výřezu pro GA4 `item_list_name` (kořen › podkategorie, výprodej skladu). */
export function sliceName(state, data, t) {
  const T = t || L;
  const f = (data && data.facets) || {};
  const parts = [];
  if (state && state.clearance) parts.push(T.clearanceTitle);
  const root = state && state.cat ? (f.roots || []).find((r) => (r.slug || slugOf(r.path)) === state.cat) : null;
  if (root) parts.push(root.name);
  const sub = state && state.sub ? (f.subs || []).find((r) => (r.slug || slugOf(r.path)) === state.sub) : null;
  if (sub) parts.push(sub.name);
  return parts.join(' › ');
}
